/* ============================================================
 * frontend-v3/src/components/MoveToFolderDialog.tsx
 *
 * NMW-931 (parent NMW-913, decision D4) — the move picker.
 *
 * DIALOG ONLY. No drag-and-drop, and no new dependency: the move verb is
 * an explicit, confirmable action so a mis-drop cannot silently re-file a
 * 200-item selection.
 *
 * Data in, mutation out:
 *   - the folder list is a PROP (the screens already subscribe to
 *     `api.folders.listFolders` for their own tree; this dialog does not
 *     open a second subscription and does not fetch),
 *   - the write goes through the LOCKED `useMutationWithToast` wrapper
 *     (V3I-INV-13), which logs `argKeys` only — never argument values.
 *
 * Backend contract (NMW-929):
 *   assets   → `api.mutations.assets.moveAssetsToFolder({ assetIds, folderId })`
 *   channels → `api.mutations.channels.moveChannelsToFolder({ channelIds, folderId })`
 *   `folderId: null` means the domain root; the batch cap is 1..100 and is
 *   enforced server-side, so a larger selection is CHUNKED here into
 *   sequential 100-item calls rather than being silently truncated.
 *
 * Fail-closed:
 *   - an unknown `domain` disables the dialog and says so (no guessing a
 *     plane and writing to the wrong table),
 *   - the folder the items already sit in is rendered disabled — a no-op
 *     write is not offered,
 *   - an empty selection cannot be confirmed.
 *
 * COMPACT by owner constraint: 26px rows, 13px type, no per-row padding
 * beyond the indent, a 320px-max scroll body. The whole tree of a working
 * library fits without a scroll on a laptop.
 * ============================================================ */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { api } from '@convex/api';
import { Btn, Icon, Modal } from './primitives';
import { useMutationWithToast } from '../hooks/useMutationWithToast';
import {
  buildFolderTree,
  type FolderDomain,
  type FolderNode,
} from '../lib/folderTree';

/** Server-side batch cap (`convex/lib/folderPlacement.ts:assertMoveBatchSize`). */
export const MOVE_BATCH_MAX = 100;

export interface MoveToFolderDialogProps {
  open: boolean;
  onClose: () => void;
  /** Which plane the selection belongs to. */
  domain: FolderDomain;
  /** The selected asset / channel ids. */
  itemIds: readonly string[];
  /** The folder the selection currently sits in; null / undefined ⇒ root. */
  currentFolderId?: string | null;
  /** Every folder of `domain` (flat). */
  folders: readonly FolderNode[];
  /** Fired after every chunk resolved; receives the total moved count. */
  onMoved?: (moved: number) => void;
}

const ROOT_TARGET = '__root__';

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

const ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  width: '100%',
  height: 26,
  padding: '0 8px',
  border: 'none',
  background: 'none',
  font: 'inherit',
  fontSize: 13,
  textAlign: 'left',
  borderRadius: 4,
  cursor: 'pointer',
  color: 'var(--ink-muted)',
};

export function MoveToFolderDialog({
  open,
  onClose,
  domain,
  itemIds,
  currentFolderId,
  folders,
  onMoved,
}: MoveToFolderDialogProps) {
  const domainOk = domain === 'asset' || domain === 'channel';

  // Both wrappers are constructed unconditionally (hook rules); only the
  // one matching `domain` is ever invoked.
  const assetMove = useMutationWithToast(api.mutations.assets.moveAssetsToFolder, {
    logLabel: 'move_assets_to_folder',
    mutationName: 'assets.moveAssetsToFolder',
    errorMessagePrefix: 'No se pudo mover',
  });
  const channelMove = useMutationWithToast(
    api.mutations.channels.moveChannelsToFolder,
    {
      logLabel: 'move_channels_to_folder',
      mutationName: 'channels.moveChannelsToFolder',
      errorMessagePrefix: 'No se pudo mover',
    },
  );

  const [target, setTarget] = useState<string>(ROOT_TARGET);
  const [busy, setBusy] = useState(false);

  // Reopening always starts from the root selection rather than whatever
  // the previous move happened to pick.
  useEffect(() => {
    if (open) setTarget(ROOT_TARGET);
  }, [open]);

  const tree = useMemo(() => buildFolderTree(folders), [folders]);
  const current = currentFolderId ?? ROOT_TARGET;
  const count = itemIds.length;
  const isNoOp = target === current;
  const canConfirm = domainOk && count > 0 && !isNoOp && !busy;

  async function confirm() {
    if (!canConfirm) return;
    const folderId = target === ROOT_TARGET ? null : target;
    setBusy(true);
    let moved = 0;
    try {
      for (const batch of chunk(itemIds, MOVE_BATCH_MAX)) {
        const result =
          domain === 'asset'
            ? await assetMove.run({
                assetIds: batch as never,
                folderId: folderId as never,
              })
            : await channelMove.run({
                channelIds: batch as never,
                folderId: folderId as never,
              });
        moved += (result as { moved?: number } | undefined)?.moved ?? batch.length;
      }
      onMoved?.(moved);
      onClose();
    } catch {
      // The wrapper already surfaced the error toast (honest-state); the
      // dialog stays open so the operator can retry or pick another target.
    } finally {
      setBusy(false);
    }
  }

  function row(id: string, name: string, indent: number, icon: string) {
    const selected = target === id;
    const disabled = id === current;
    return (
      <button
        key={id}
        type="button"
        data-testid="move-target"
        data-folder-id={id}
        data-selected={selected ? '' : undefined}
        aria-pressed={selected}
        disabled={disabled}
        title={disabled ? 'Los elementos ya están aquí' : name}
        onClick={() => setTarget(id)}
        style={{
          ...ROW,
          paddingLeft: 8 + indent * 14,
          background: selected ? 'var(--lightest-gray)' : 'transparent',
          fontWeight: selected ? 500 : 400,
          opacity: disabled ? 0.45 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Icon name={icon} size={13} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </span>
        {disabled && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted-slate)' }}>
            actual
          </span>
        )}
        {selected && !disabled && (
          <Icon name="check" size={13} style={{ marginLeft: 'auto' }} />
        )}
      </button>
    );
  }

  const rootLabel = domain === 'channel' ? 'Canales (raíz)' : 'Biblioteca (raíz)';

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={420}
      title={
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>
          Mover {count} {count === 1 ? 'elemento' : 'elementos'}
        </h2>
      }
      footer={
        <>
          <Btn size="sm" variant="ghost" onClick={onClose}>
            Cancelar
          </Btn>
          <Btn
            size="sm"
            variant="primary"
            onClick={confirm}
            disabled={!canConfirm}
            loading={busy}
          >
            Mover
          </Btn>
        </>
      }
    >
      {!domainOk ? (
        <p data-testid="move-domain-error" style={{ margin: 0, fontSize: 13, color: 'var(--bad)' }}>
          Dominio de carpeta desconocido: no se puede mover.
        </p>
      ) : (
        <div data-testid="move-tree" style={{ maxHeight: 320, overflow: 'auto' }}>
          {row(ROOT_TARGET, rootLabel, 0, 'home')}
          {tree.map(({ folder, children }) => (
            <div key={folder._id}>
              {row(folder._id, folder.name, 1, 'folder')}
              {children.map((child) => row(child._id, child.name, 2, 'folder'))}
            </div>
          ))}
          {tree.length === 0 && (
            <p style={{ margin: '6px 8px 0', fontSize: 12, color: 'var(--muted-slate)' }}>
              Aún no hay carpetas en este dominio.
            </p>
          )}
          {count > MOVE_BATCH_MAX && (
            <p style={{ margin: '8px 8px 0', fontSize: 11, color: 'var(--muted-slate)' }}>
              {count} elementos se moverán en {Math.ceil(count / MOVE_BATCH_MAX)} lotes de{' '}
              {MOVE_BATCH_MAX}.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

export default MoveToFolderDialog;
